import { useUser } from "@clerk/tanstack-react-start"
import * as Sentry from "@sentry/react"
import { api } from "@stoneflow/backend/convex/_generated/api"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useAction } from "convex/react"
import { useCallback, useEffect, useState } from "react"
import { toast } from "sonner"
import { RouteError } from "#/components/route-error"
import { Badge } from "#/components/ui/badge"
import { Button } from "#/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card"
import { Input } from "#/components/ui/input"
import { Skeleton } from "#/components/ui/skeleton"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "#/components/ui/table"
import { useDebounce } from "#/hooks/useDebounce"

export const Route = createFileRoute("/_authenticated/admin/staff")({
	beforeLoad: ({ context }) => {
		const roles = (context as { roles?: string[] }).roles ?? []
		if (!roles.includes("platform_admin")) {
			throw redirect({ to: "/admin" })
		}
	},
	errorComponent: RouteError,
	component: StaffPage,
})

type StaffUser = {
	clerkId: string
	firstName: string | null
	lastName: string | null
	email: string | null
	orgName: string | null
	orgId: string | null
	platformRoles: string[]
}

type TargetRole = "platform_admin" | "platform_operator" | "none"

function displayName(u: StaffUser): string {
	const name = [u.firstName, u.lastName].filter(Boolean).join(" ")
	return name || u.email || u.clerkId
}

function transitionMessage(user: StaffUser, role: TargetRole): string {
	const name = displayName(user)
	if (role === "platform_admin") return `Promoted ${name} to Platform Admin`
	if (role === "platform_operator") {
		return user.platformRoles.includes("platform_admin")
			? `Demoted ${name} to Platform Operator`
			: `Granted Platform Operator to ${name}`
	}
	return `Revoked platform access from ${name}`
}

function StaffPage() {
	const { user: currentUser } = useUser()
	const currentUserId = currentUser?.id ?? null
	const listStaff = useAction(api.platform.adminActions.listPlatformStaff)
	const searchUsers = useAction(api.platform.adminActions.searchUsers)
	const setRole = useAction(api.platform.adminActions.setUserPlatformRole)

	const [staff, setStaff] = useState<StaffUser[] | null>(null)
	const [searchQuery, setSearchQuery] = useState("")
	const [searchResults, setSearchResults] = useState<StaffUser[] | null>(null)
	const [searching, setSearching] = useState(false)
	const [pendingUserId, setPendingUserId] = useState<string | null>(null)
	const debouncedQuery = useDebounce(searchQuery, 300)

	const loadStaff = useCallback(async () => {
		try {
			const result = await listStaff()
			setStaff(result)
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to load staff")
		}
	}, [listStaff])

	useEffect(() => {
		loadStaff()
	}, [loadStaff])

	useEffect(() => {
		if (!debouncedQuery.trim()) {
			setSearchResults(null)
			return
		}
		let cancelled = false
		setSearching(true)
		searchUsers({ query: debouncedQuery })
			.then((result) => {
				if (!cancelled) setSearchResults(result)
			})
			.catch((err) => {
				if (!cancelled) {
					Sentry.captureException(err)
					toast.error(err instanceof Error ? err.message : "Search failed")
				}
			})
			.finally(() => {
				if (!cancelled) setSearching(false)
			})
		return () => {
			cancelled = true
		}
	}, [debouncedQuery, searchUsers])

	async function handleSetRole(user: StaffUser, role: TargetRole) {
		setPendingUserId(user.clerkId)
		try {
			await setRole({ clerkUserId: user.clerkId, role })
			toast.success(transitionMessage(user, role))
			await loadStaff()
			if (debouncedQuery.trim()) {
				const refreshed = await searchUsers({ query: debouncedQuery })
				setSearchResults(refreshed)
			}
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to update role")
		} finally {
			setPendingUserId(null)
		}
	}

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Current platform staff</CardTitle>
				</CardHeader>
				<CardContent>
					{staff === null ? (
						<div className="space-y-2">
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-10 w-full" />
						</div>
					) : staff.length === 0 ? (
						<p className="text-sm text-foreground-secondary">
							No platform staff found. Grant operators access via the search below.
						</p>
					) : (
						<StaffTable
							users={staff}
							currentUserId={currentUserId}
							pendingUserId={pendingUserId}
							onSetRole={handleSetRole}
						/>
					)}
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Grant Platform Operator</CardTitle>
					<p className="text-sm text-foreground-secondary">
						Search Clerk users by name or email to grant operator access. Promote or demote existing
						staff from the list above. You can't change your own role here — use the Clerk
						dashboard.
					</p>
				</CardHeader>
				<CardContent className="space-y-4">
					<Input
						placeholder="Search by name or email…"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
					{searching ? (
						<div className="space-y-2">
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-10 w-full" />
						</div>
					) : searchResults === null ? (
						<p className="text-sm text-foreground-muted">Type at least one character to search.</p>
					) : searchResults.length === 0 ? (
						<p className="text-sm text-foreground-secondary">No matching users.</p>
					) : (
						<StaffTable
							users={searchResults}
							currentUserId={currentUserId}
							pendingUserId={pendingUserId}
							onSetRole={handleSetRole}
						/>
					)}
				</CardContent>
			</Card>
		</div>
	)
}

function StaffTable({
	users,
	currentUserId,
	pendingUserId,
	onSetRole,
}: {
	users: StaffUser[]
	currentUserId: string | null
	pendingUserId: string | null
	onSetRole: (user: StaffUser, role: TargetRole) => void
}) {
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Name</TableHead>
					<TableHead>Email</TableHead>
					<TableHead>Tenant</TableHead>
					<TableHead>Platform role</TableHead>
					<TableHead className="text-right">Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{users.map((user) => {
					const isAdmin = user.platformRoles.includes("platform_admin")
					const isOperator = user.platformRoles.includes("platform_operator")
					const isPending = pendingUserId === user.clerkId
					const isSelf = currentUserId !== null && user.clerkId === currentUserId
					return (
						<TableRow key={user.clerkId}>
							<TableCell className="font-medium">{displayName(user)}</TableCell>
							<TableCell className="text-foreground-secondary">{user.email ?? "—"}</TableCell>
							<TableCell className="text-foreground-secondary">{user.orgName ?? "—"}</TableCell>
							<TableCell>
								<div className="flex flex-wrap gap-1">
									{isAdmin && (
										<Badge variant="outline" className="border-severe text-severe">
											Platform Admin
										</Badge>
									)}
									{isOperator && (
										<Badge variant="outline" className="border-info text-info">
											Platform Operator
										</Badge>
									)}
									{!isAdmin && !isOperator && (
										<span className="text-xs text-foreground-muted">—</span>
									)}
								</div>
							</TableCell>
							<TableCell className="text-right">
								{isSelf ? (
									<span className="text-xs text-foreground-muted">You — change in Clerk</span>
								) : isAdmin ? (
									<Button
										variant="outline"
										size="sm"
										disabled={isPending}
										onClick={() => onSetRole(user, "platform_operator")}
									>
										Demote to Operator
									</Button>
								) : isOperator ? (
									<div className="flex justify-end gap-2">
										<Button
											variant="outline"
											size="sm"
											disabled={isPending}
											onClick={() => onSetRole(user, "platform_admin")}
										>
											Promote to Admin
										</Button>
										<Button
											variant="destructive"
											size="sm"
											disabled={isPending}
											onClick={() => onSetRole(user, "none")}
										>
											Revoke
										</Button>
									</div>
								) : (
									<Button
										variant="outline"
										size="sm"
										disabled={isPending}
										onClick={() => onSetRole(user, "platform_operator")}
									>
										Grant Operator
									</Button>
								)}
							</TableCell>
						</TableRow>
					)
				})}
			</TableBody>
		</Table>
	)
}
